import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Sparkles, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PnlImportDialog = ({ open, onClose, onImportComplete }) => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [previewData, setPreviewData] = useState(null);
  const [categorizations, setCategorizations] = useState([]);
  const [loadingCat, setLoadingCat] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => { setStep(1); setPreviewData(null); setCategorizations([]); };
  const handleOpenChange = (isOpen) => { if (!isOpen) { reset(); onClose(); } };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('year', year);
    try {
      const res = await axios.post(`${API}/csv/pnl/preview`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      setPreviewData(res.data);
      setStep(2);
      toast.success(t('toast.parsed_items', { count: res.data.summary.total_rows }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('toast.failed_to_parse_pnl'));
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCategorize = async () => {
    if (!previewData?.parsed_rows) return;
    setLoadingCat(true);
    const lineItems = previewData.parsed_rows.map(r => r.line_item);
    try {
      const fd = new FormData();
      fd.append('line_items', JSON.stringify(lineItems));
      const res = await axios.post(`${API}/csv/pnl/categorize`, fd, { headers: getAuthHeaders(), withCredentials: true });
      setCategorizations(res.data.categorizations || []);
      setStep(3);
      toast.success(t('toast.ai_categorization_complete'));
    } catch {
      setCategorizations(lineItems.map(item => ({ line_item: item, category: 'other', confidence: 0.5 })));
      setStep(3);
    } finally {
      setLoadingCat(false);
    }
  };

  const handleImport = async () => {
    if (!previewData?.csv_content) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('csv_content', previewData.csv_content);
      fd.append('year', year);
      fd.append('categorizations', JSON.stringify(categorizations));
      const res = await axios.post(`${API}/csv/pnl/import`, fd, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
      reset();
      onClose();
      onImportComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('toast.pnl_import_failed'));
    } finally {
      setImporting(false);
    }
  };

  const years = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 5 + i);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Import P&L CSV — Step {step} of 3
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Upload your Profit & Loss CSV (Category in Column A, Months in B-M)'}
            {step === 2 && 'AI will categorize your line items to Schedule C categories'}
            {step === 3 && 'Review categorizations and import as drafts'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4 mb-2">
              <Label>Import Year:</Label>
              <Select value={year.toString()} onValueChange={v => setYear(parseInt(v))}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="border-2 border-dashed border-emerald-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors bg-emerald-50/50">
              <input type="file" ref={fileRef} accept=".csv" onChange={handleFile} className="hidden" id="pnl-file-input" />
              <label htmlFor="pnl-file-input" className="cursor-pointer">
                <TrendingUp className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
                <p className="text-lg font-medium text-gray-700">Click to upload P&L CSV</p>
                <p className="text-sm text-gray-500 mt-1">Profit & Loss statement with monthly data</p>
              </label>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800 space-y-1">
              <p className="font-medium">Expected Format:</p>
              <p>• Column A: Category/Line Item Name</p>
              <p>• Columns B-M: Monthly amounts (Jan-Dec)</p>
              <p>• Positive numbers = Income, Negative = Expenses</p>
            </div>
          </div>
        )}

        {step === 2 && previewData && (
          <div className="space-y-4 py-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-600">${previewData.summary.total_income.toLocaleString()}</p>
                  <p className="text-xs text-gray-600">Total Income</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">${previewData.summary.total_expenses.toLocaleString()}</p>
                  <p className="text-xs text-gray-600">Total Expenses</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">${previewData.summary.net_income.toLocaleString()}</p>
                  <p className="text-xs text-gray-600">Net Income</p>
                </div>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Line Item</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-center p-2">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.parsed_rows.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">{row.line_item}</td>
                      <td className={`p-2 text-right font-mono ${row.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                        ${Math.abs(row.calculated_total).toLocaleString()}
                      </td>
                      <td className="p-2 text-center">
                        <Badge variant={row.type === 'income' ? 'default' : 'secondary'}>{row.type}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.parsed_rows.length > 10 && (
                <p className="text-xs text-gray-500 p-2 text-center">+{previewData.parsed_rows.length - 10} more items</p>
              )}
            </div>
            <Button onClick={handleCategorize} disabled={loadingCat} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {loadingCat
                ? <><Sparkles className="w-4 h-4 mr-2 animate-pulse" />AI Categorizing...</>
                : <><Sparkles className="w-4 h-4 mr-2" />Auto-Categorize with AI</>}
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-4">
            <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Line Item</th>
                    <th className="text-left p-2">Category</th>
                    <th className="text-center p-2">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {categorizations.map((c, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">{c.line_item}</td>
                      <td className="p-2"><Badge variant="outline" className="bg-blue-50">{c.category}</Badge></td>
                      <td className="p-2 text-center">
                        <span className={`text-xs ${c.confidence > 0.8 ? 'text-emerald-600' : c.confidence > 0.5 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {Math.round(c.confidence * 100)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              <strong>Note:</strong> All transactions will be imported as <Badge variant="secondary">Draft</Badge>. Review before finalizing.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Back</Button>
              <Button onClick={handleImport} disabled={importing} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {importing ? 'Importing...' : 'Import as Drafts'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
